/**
 * Analytics page - Full analytics dashboard (Epic 6)
 * 
 * Per design.md: Use @uprosal/ui components, semantic tokens, lucide icons
 */

import { useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../../../../convex/_generated/api';
import { motion } from 'framer-motion';
import {
    Card, CardContent, CardHeader, CardTitle,
    Button, Select,
    Download,
} from '@uprosal/ui';
import { BlurFade } from '../../components/BlurFade';
import { NumberTicker } from '../../components/NumberTicker';

type DateRange = 7 | 30 | 90;
type ChartDataPoint = { date: string; generated: number; sent: number };

export function AnalyticsPage() {
    const [days, setDays] = useState<DateRange>(30);

    // Fetch real data
    const chartData = useQuery(api.queries.analytics.getChartData, { days }) as ChartDataPoint[] | undefined;
    const stats = useQuery(api.queries.analytics.getAnalyticsStats);

    // Generate CSV for export
    const handleExport = () => {
        if (!chartData) return;

        const headers = ['Date', 'Generated', 'Sent'];
        const rows = chartData.map(d => [d.date, d.generated, d.sent].join(','));
        const csv = [headers.join(','), ...rows].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `upzora-analytics-${days}d.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Calculate max value for chart scaling
    const maxValue = useMemo(() => {
        if (!chartData) return 10;
        return Math.max(...chartData.map(d => d.generated), 1);
    }, [chartData]);

    const isLoading = chartData === undefined || stats === undefined;

    return (
        <div className="space-y-6">
            {/* Header */}
            <BlurFade delay={0}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
                        <p className="text-muted-foreground mt-1">
                            Track your proposal performance and AI usage
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select
                            value={days.toString()}
                            onChange={(e) => setDays(parseInt(e.target.value) as DateRange)}
                        >
                            <option value="7">Last 7 days</option>
                            <option value="30">Last 30 days</option>
                            <option value="90">Last 90 days</option>
                        </Select>
                        <Button
                            variant="outline"
                            className="flex items-center gap-2"
                            onClick={handleExport}
                            disabled={!chartData}
                        >
                            <Download className="w-4 h-4" />
                            Export
                        </Button>
                    </div>
                </div>
            </BlurFade>

            {/* Chart */}
            <BlurFade delay={0.1}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg">
                                Proposals Over Time
                            </CardTitle>
                            <div className="flex items-center gap-4">
                                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span className="w-3 h-3 rounded-full bg-primary" />
                                    Generated
                                </span>
                                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span className="w-3 h-3 rounded-full bg-chart-1" />
                                    Sent
                                </span>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <div className="h-64 flex items-center justify-center">
                                    <span className="text-muted-foreground">Loading...</span>
                                </div>
                            ) : chartData.length === 0 ? (
                                <div className="h-64 flex items-center justify-center">
                                    <span className="text-muted-foreground">No data for this period</span>
                                </div>
                            ) : (
                                <div className="h-64 flex flex-col gap-4">
                                    <div className="flex items-end justify-around h-full gap-1 px-4">
                                        {chartData.map((d, i) => (
                                            <div key={i} className="flex-1 flex flex-col items-center gap-1 max-w-8">
                                                <motion.div
                                                    className="w-full rounded-t bg-primary"
                                                    style={{ minHeight: 4 }}
                                                    initial={{ height: 0 }}
                                                    animate={{ height: `${Math.max((d.generated / maxValue) * 100, 2)}%` }}
                                                    transition={{ delay: i * 0.02, duration: 0.4 }}
                                                    title={`Generated: ${d.generated}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex justify-around text-xs text-muted-foreground px-4 overflow-hidden">
                                        {chartData.filter((_, i) => i % Math.ceil(chartData.length / 7) === 0).map((d, i) => (
                                            <span key={i}>{d.date}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
            </BlurFade>

            {/* Stats grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <BlurFade delay={0.2}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                    >
                        <Card>
                            <CardContent className="pt-6">
                                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                                    Most Used Prompt
                                </h3>
                                <p className="text-xl font-bold text-foreground mb-1">
                                    {stats?.mostUsedPrompt.name || 'Default'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Used {stats?.mostUsedPrompt.count || 0} times this week
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </BlurFade>

                <BlurFade delay={0.3}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                    >
                        <Card>
                            <CardContent className="pt-6">
                                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                                    Best Fit Score Category
                                </h3>
                                <p className="text-xl font-bold text-foreground mb-1">
                                    {stats?.bestCategory.name || 'N/A'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Avg fit score: {stats?.bestCategory.avgFitScore || 0}%
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </BlurFade>

                <BlurFade delay={0.4}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <Card>
                            <CardContent className="pt-6">
                                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                                    Token Efficiency
                                </h3>
                                <p className="text-xl font-bold text-foreground mb-1">
                                    <NumberTicker value={stats?.tokenEfficiency.avgTokens || 0} /> tokens/proposal
                                </p>
                                <p className={`text-sm ${(stats?.tokenEfficiency.delta || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                    {(stats?.tokenEfficiency.delta || 0) >= 0 ? '+' : ''}{stats?.tokenEfficiency.delta || 0}% vs last week
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </BlurFade>
            </div>
        </div>
    );
}
